import { EnvelopeIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { Spinner } from "@via/components/loading/loading";
import Modal from "@via/components/modal";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import Table, { TableSize } from "@via/components/table/table";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import { useDeleteInvitation } from "@via/pages/account/admin/admin.api.tsx";
import {
  useDeleteUserGroupMember,
  useUserGroupMembers,
} from "@via/api/user-groups.api.tsx";
import { useShareProject } from "@via/pages/projects/projects.api.tsx";
import { ERROR, ModalSize } from "@via/utils/constants";
import { userInvitationOptions } from "@via/utils/form-validators";
import { GetUserGroupMemberResponse, Permissions } from "common";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";

interface ShareProjectModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
  groupId?: number | null; // default selection for Group tab
  sharedGroupId?: number | null; // source group to pre-populate Specific Users
  permission?: Permissions; // used to set initial tab
}

const columnHelper = createColumnHelper<GetUserGroupMemberResponse>();

const defaultParams = {
  sort: [{ id: "id", desc: true }],
  page: 0,
  pageSize: 10,
};

const ShareProjectModal: FC<ShareProjectModalProps> = ({
  open,
  setOpen,
  projectId = "",
  groupId,
  sharedGroupId,
  permission,
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  const [activeTab, setActiveTab] = useState("Group");
  const [page, setPage] = useState(defaultParams.page);
  const [pageSize, setPageSize] = useState(defaultParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    defaultParams.sort
  );
  const [addedEmails, setAddedEmails] = useState<string[]>([]);
  const [deletingEmail, setDeletingEmail] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      groupId: Number(groupId) || undefined,
      email: "",
      sendNotificationToUsersInGroup: true,
    },
  });

  const selectedGroupId = watch("groupId");

  // GROUP TAB fetch
  const {
    data: userGroupMembers,
    isLoading: isUserGroupMembersLoading,
    isError: isUserGroupMembersError,
  } = useUserGroupMembers({
    groupId: selectedGroupId,
    page,
    pageSize,
    sort,
    includePending: true,
    enabled: activeTab === "Group" && !!selectedGroupId,
  });

  // SPECIFIC USERS TAB pre-population fetch (if sharedGroupId exists)
  const {
    data: sharedGroupMembers,
    isLoading: isSharedGroupMembersLoading,
    isError: isSharedGroupMembersError,
  } = useUserGroupMembers({
    groupId: sharedGroupId ?? undefined,
    page: 0,
    pageSize: 100,
    sort: [{ id: "id", desc: true }],
    includePending: true,
    enabled: activeTab === "Specific Users" && !!sharedGroupId,
  });

  const { mutateAsync: shareProject, isPending: isShareProjectLoading } =
    useShareProject({ projectId });

  const {
    mutateAsync: deleteInvitation,
    isPending: isDeleteInvitationLoading,
  } = useDeleteInvitation();
  const { mutateAsync: deleteUser, isPending: isDeleteUserLoading } =
    useDeleteUserGroupMember();

  useEffect(() => {
    setActiveTab(
      permission === Permissions.SpecificUsersShared
        ? "Specific Users"
        : "Group"
    );
  }, [permission]);

  // Helpers
  const isLocalEmailRow = (r: GetUserGroupMemberResponse) =>
    typeof (r as { id?: unknown }).id === "string" &&
    String((r as { id: string }).id || "").startsWith("email:");

  const StatusCell: FC<{ row: GetUserGroupMemberResponse }> = ({ row }) => {
    // local rows come from "addedEmails" and have an "email:<value>" id
    const local = isLocalEmailRow(row);
    if (local) {
      // ensure a unique anchor per row for the tooltip
      const anchorId = `status-local-${String(row.id).replace(/[^a-zA-Z0-9_-]/g, "_")}`;
      return (
        <>
          <span className="flex items-center">
            <EnvelopeIcon id={anchorId} className="h-5 w-5 text-gray-500" />
          </span>
          <Tooltip
            anchorSelect={`#${anchorId}`}
            content="User will receive an invitation email."
          />
        </>
      );
    }

    // Non-local: show status pill (Pending vs Active)
    return row.isPending ? (
      <span className="rounded-full px-2 py-1 text-xs font-semibold border-1 border-gray-300">
        Pending
      </span>
    ) : (
      <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
        Active
      </span>
    );
  };

  // Group table columns
  const groupColumns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: i => i.getValue(),
        header: () => <span>Name</span>,
      }),
      columnHelper.accessor("userName", {
        cell: i => i.getValue(),
        header: () => <span>Username</span>,
      }),
      columnHelper.accessor("email", {
        cell: i => i.getValue(),
        header: () => <span>E-mail</span>,
      }),
      columnHelper.display({
        id: "status",
        header: () => <span>Status</span>,
        cell: ({ row }) => <StatusCell row={row.original} />,
      }),
    ],
    []
  ) as ColumnDef<GetUserGroupMemberResponse>[];

  const groupTableData: GetUserGroupMemberResponse[] =
    userGroupMembers?.data ?? [];

  // Specific Users data
  const sharedGroupRows: GetUserGroupMemberResponse[] =
    sharedGroupMembers?.data ?? [];
  const localEmailRows: GetUserGroupMemberResponse[] = addedEmails.map(e => ({
    id: `email:${e}`,
    name: "",
    userName: "",
    email: e,
  }));

  const usersTableData: GetUserGroupMemberResponse[] = useMemo(() => {
    if (!sharedGroupId) return localEmailRows;
    const sharedEmails = new Set(
      sharedGroupRows.map(r => (r.email || "").toLowerCase()).filter(Boolean)
    );
    const onlyLocalNew = localEmailRows.filter(
      r => !sharedEmails.has((r.email || "").toLowerCase())
    );
    return [...sharedGroupRows, ...onlyLocalNew];
  }, [sharedGroupId, sharedGroupRows, localEmailRows]);

  // Remove handler for Specific Users table
  const handleRemoveRow = async (row: GetUserGroupMemberResponse) => {
    const email = (row.email || "").toLowerCase();
    if (!email) return;

    // Local row -> remove from local list
    if (isLocalEmailRow(row)) {
      setAddedEmails(prev => prev.filter(e => e.toLowerCase() !== email));
      return;
    }

    // Non-local row: must have sharedGroupId (prepopulated)
    try {
      setDeletingEmail(email);
      if (row.isPending) {
        // pending -> delete invitation
        await deleteInvitation({ id: Number(row.id) });
      } else {
        // active -> delete user from the shared group
        if (!sharedGroupId) return; // safety
        await deleteUser({
          userId: String(row.id),
          groupId: String(sharedGroupId),
        });
      }
    } finally {
      setDeletingEmail(null);
    }
  };

  // Specific Users columns (Name, Username, Email, Status, Actions)
  const usersColumns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: i => i.getValue(),
        header: () => <span>Name</span>,
      }),
      columnHelper.accessor("userName", {
        cell: i => i.getValue(),
        header: () => <span>Username</span>,
      }),
      columnHelper.accessor("email", {
        cell: i => i.getValue(),
        header: () => <span>E-mail</span>,
      }),
      columnHelper.display({
        id: "status",
        header: () => <span>Status</span>,
        cell: ({ row }) => <StatusCell row={row.original} />,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const email = (row.original.email || "").toLowerCase();
          const disabled =
            !!email &&
            deletingEmail === email &&
            (isDeleteInvitationLoading || isDeleteUserLoading);

          return (
            <div className="flex h-full items-center justify-center">
              <button
                type="button"
                onClick={() => handleRemoveRow(row.original)}
                aria-label={`Remove ${row.original.email ?? ""}`}
                disabled={disabled}
                className="inline-flex h-7 w-7 -my-1 items-center justify-center rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-60">
                {disabled ? (
                  <Spinner loading />
                ) : (
                  <TrashIcon className="h-4 w-4 text-red-400" />
                )}
              </button>
            </div>
          );
        },
      }),
    ],
    [deletingEmail, isDeleteInvitationLoading, isDeleteUserLoading]
  ) as ColumnDef<GetUserGroupMemberResponse>[];

  // Submit handlers
  const handleShareByGroup = async payload => {
    if (!selectedGroupId) {
      setError("groupId", { type: "manual", message: "Select a group." });
      return;
    }
    await shareProject({
      groupId: selectedGroupId,
      sendNotificationToUsersInGroup: payload.sendNotificationToUsersInGroup,
    });
    setOpen(false);
  };

  const handleShareByEmails = async () => {
    if (addedEmails.length === 0) {
      setOpen(false);
      return;
    }
    const emailsToSend = addedEmails.map(e => e.trim()).filter(Boolean);
    await shareProject({ emails: emailsToSend });
    setOpen(false);
  };

  const onSubmit = handleSubmit(payload =>
    activeTab === "Group" ? handleShareByGroup(payload) : handleShareByEmails()
  );

  // Confirm guard
  const handleConfirmModal = async (): Promise<boolean> => {
    const canSubmit =
      (activeTab === "Group" && !!selectedGroupId) ||
      (activeTab === "Specific Users" && addedEmails.length > 0);
    if (!canSubmit) {
      if (activeTab === "Group") {
        // Nothing was shared, so the modal must stay open with the error just set on the field.
        setError("groupId", { type: "manual", message: "Select a group." });
        return false;
      }
      // Nothing to send on this tab, so the confirm is a dismissal. Modal owns the close.
      return true;
    }
    // Fire and DO NOT report success: the real work is the submit handler's async share call, and
    // it closes the modal itself once that call resolves. Reporting success here closed the modal
    // the moment the event was dispatched, so a share that then FAILED left the user with no
    // modal, no error and no idea whether it worked. (#450)
    formRef.current?.dispatchEvent(
      new Event("submit", { cancelable: true, bubbles: true })
    );
    return false;
  };

  // Add email
  const addEmail = async () => {
    const valid = await trigger("email");
    const value = (getValues("email") as string)?.trim().toLowerCase();
    if (!valid || !value) return;

    const inLocal = addedEmails.some(e => e.toLowerCase() === value);
    const inShared = (sharedGroupRows ?? []).some(
      m => (m.email || "").toLowerCase() === value
    );
    if (inLocal || inShared) {
      setError("email", {
        type: "manual",
        message: "This email is already listed.",
      });
      return;
    }
    setAddedEmails(prev => [...prev, value]);
    clearErrors("email");
    setValue("email", "");
  };

  const isConfirmLoading =
    isShareProjectLoading ||
    (activeTab === "Group"
      ? isUserGroupMembersLoading
      : isSharedGroupMembersLoading);

  const isConfirmDisabled =
    isConfirmLoading ||
    (activeTab === "Group" ? !selectedGroupId : usersTableData.length === 0);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Share Project"
      isConfirmLoading={isConfirmLoading}
      isConfirmDisabled={isConfirmDisabled}
      size={ModalSize.Large}
      onConfirm={handleConfirmModal}
      confirmText="Share">
      <form
        ref={formRef}
        onSubmit={e => {
          e.preventDefault();
          onSubmit(e);
        }}>
        <HorizontalTabs
          buttonIdPrefix="share-project"
          activeTab={activeTab}
          onTabChange={setActiveTab}
          options={[
            {
              name: "Group",
              children: (
                <div className="pb-5 pt-4">
                  <UserGroupSelectComponent
                    control={control}
                    name="groupId"
                    menuPlacement="bottom"
                    placeholder="Share with a group"
                    missingId={groupId ? [groupId] : []}
                    enableClear
                  />
                  {selectedGroupId && (
                    <>
                      {isUserGroupMembersError ? (
                        <div className="via-generic-error">
                          <GenericError message={ERROR} />
                        </div>
                      ) : (
                        <>
                          <div className="mt-2 text-sm text-gray-500">
                            Sharing project with members of the selected group.
                          </div>
                          <Table
                            isLoading={isUserGroupMembersLoading}
                            loaderCount={3}
                            data={groupTableData}
                            columns={groupColumns}
                            sort={sort}
                            setSort={setSort}
                            page={page}
                            setPage={setPage}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            total={userGroupMembers?.total ?? 0}
                            size={TableSize.Small}
                          />
                          <label className="flex items-center gap-2 mt-6">
                            <FormInputComponent
                              control={control}
                              name="sendNotificationToUsersInGroup"
                              type="checkbox"
                              className="h-6 w-6 -mt-2"
                            />
                            <div className="whitespace-nowrap text-sm font-medium leading-6 text-gray-900">
                              Send Invitation Emails to Group Members
                            </div>
                          </label>
                        </>
                      )}
                    </>
                  )}
                </div>
              ),
            },
            {
              name: "Specific Users",
              children: (
                <div className="pb-5 pt-2">
                  <div className="flex gap-2 items-start">
                    <div className="flex-1">
                      <FormInputComponent
                        control={control}
                        name="email"
                        label=""
                        autoComplete="off"
                        placeholder="Enter email address"
                        validations={userInvitationOptions.email}
                        onKeyDown={(
                          e: React.KeyboardEvent<HTMLInputElement>
                        ) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void addEmail();
                          }
                        }}
                      />
                    </div>
                    <button
                      id="add-member"
                      type="button"
                      onClick={addEmail}
                      className="mt-2 whitespace-nowrap bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md inline-flex gap-x-1.5 rounded-md px-3 py-2 text-sm shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      Add Member
                    </button>
                  </div>

                  {isSharedGroupMembersError ? (
                    <div className="via-generic-error mt-3">
                      <GenericError message={ERROR} />
                    </div>
                  ) : (
                    usersTableData.length > 0 && (
                      <>
                        <div className="mt-2 text-sm text-gray-500">
                          Sharing project with the following users.
                        </div>
                        <Table
                          isLoading={
                            isSharedGroupMembersLoading && !!sharedGroupId
                          }
                          loaderCount={3}
                          data={usersTableData}
                          columns={usersColumns}
                          total={usersTableData.length}
                          size={TableSize.Small}
                        />
                      </>
                    )
                  )}
                </div>
              ),
            },
          ]}
        />
      </form>
    </Modal>
  );
};

export default ShareProjectModal;
